import React, { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Sparkles,
  Package,
  Package2,
  Users,
  ShoppingCart,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  UserCheck,
  Users2,
  FileText,
  DollarSign,
  Clock,
  Menu,
  X,
  BookOpen,
  Code,
  ExternalLink,
  CreditCard,
  Percent,
  Shield,
  Sun,
  Moon,
  Monitor,
  Receipt,
  Truck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Flag,
  GraduationCap,
  FlaskConical,
  Store,
  Folder,
  MessageSquare,
  Stethoscope,
  Tag,
  KeyRound,
  HeartPulse,
} from 'lucide-react';
import { UserRole } from '@bloom/shared';
import { useUser, useAuthStore, useTeamMember } from '../../store/useAuthStore';
import { NotificationBell } from '../notifications/NotificationBell';
import { useTenantBranding } from '../../hooks/useTenantBranding-runtime';
import { ImpersonationBanner } from '../impersonation/ImpersonationBanner';
import { AppBanners } from './AppBanners';
import { ZendeskWidget } from '../support/ZendeskWidget';
import { useTheme } from '../../hooks/useTheme';
import { apiClient } from '../../lib/api';
import { useActivityTracker } from '../../hooks/useActivityTracker';
import { useCareInboxUnreadCount } from '../../hooks/useCareInboxUnreadCount';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  roles: UserRole[];
  // Optional extra predicate beyond the role check. Receives the current
  // user — useful for per-clinic feature flags (e.g. membershipEnabled).
  visibleIf?: (user: any) => boolean;
  // Served outside the SPA (e.g. the backend-rendered API reference), so it
  // opens in a new tab rather than going through the router.
  external?: boolean;
  // Sub-pages nested under this item, collapsed by default and opened with
  // the chevron (or automatically while one of them is the current page).
  // A child the viewer can see is promoted to the top level when the parent
  // itself is hidden from them.
  children?: NavItem[];
}

/** An item and its children, flattened: for route matching and page titles. */
const flattenNav = (items: NavItem[]): NavItem[] =>
  items.flatMap(item => [item, ...(item.children ?? [])]);

interface NavCategory {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
  defaultOpen?: boolean;
}

// Standalone items (not in a folder)
const standaloneItems: NavItem[] = [
  {
    label: 'Home',
    icon: Home,
    href: '/dashboard',
    roles: ['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly'],
  },
  {
    label: 'Ava',
    icon: Sparkles,
    href: '/ava',
    roles: ['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly'],
    // On unless an admin turned Ava off for this user (User.avaEnabled === false).
    visibleIf: (u: any) => u?.avaEnabled !== false,
  },
];

// Categorized navigation items
const navigationCategories: NavCategory[] = [
  {
    label: 'Orders',
    icon: ShoppingCart,
    defaultOpen: true,
    items: [
      {
        label: 'All Orders',
        icon: ShoppingCart,
        href: '/admin/orders',
        roles: ['admin'],
      },
      {
        label: 'Fulfillment',
        icon: Truck,
        href: '/admin/fulfillment',
        roles: ['admin'] as UserRole[],
      },
      {
        label: 'Patient Orders',
        icon: ShoppingCart,
        href: '/orders',
        roles: ['physician', 'physician_readonly'],
      },
      {
        label: 'Bulk Orders',
        icon: Package2,
        href: '/bulk-orders/list',
        roles: ['admin', 'distributor', 'supervisor', 'physician'],
      },
      {
        label: 'Invoice Mgmt',
        icon: DollarSign,
        href: '/admin/invoices',
        roles: ['admin'],
      },
      {
        label: 'Invoice Tracking',
        icon: FileText,
        href: '/invoice-tracking',
        roles: ['supervisor', 'distributor', 'physician'],
      },
      {
        label: 'Patients',
        icon: Users,
        href: '/patients',
        roles: ['physician', 'physician_readonly'],
      },
      {
        // Patient portal messages and refill requests.
        label: 'Care Inbox',
        icon: MessageSquare,
        href: '/care-inbox',
        roles: ['physician', 'physician_readonly'],
      },
      {
        // Pharmacy accounts only: scripts their doctors transferred in to fill.
        label: 'Rx Transfers',
        icon: FileText,
        href: '/prescription-transfers',
        roles: ['physician'],
        visibleIf: (user: any) => user?.physicianData?.isPharmacy === true,
      },
      {
        label: 'My Store',
        icon: Store,
        href: '/store',
        roles: ['physician', 'physician_readonly'],
      },
      {
        // How the clinic's own patients find the store and use the portal.
        label: 'Patient Insights',
        icon: BarChart3,
        href: '/patient-insights',
        roles: ['physician', 'physician_readonly'],
      },
      { label: 'Clinic Domain', icon: Store, href: '/clinic-site', roles: ['physician'], visibleIf: (user: any) => user?.physicianData?.customDomainEnabled === true },
      {
        label: 'Membership Reviews',
        icon: Users,
        href: '/memberships/pending',
        roles: ['physician'],
        visibleIf: (u) => u?.physicianData?.membershipEnabled === true,
      },
    ],
  },
  {
    label: 'Products',
    icon: Package,
    defaultOpen: true,
    items: [
      {
        label: 'Products',
        icon: Package,
        href: '/products',
        roles: ['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly'],
      },
      {
        label: 'Treatment Programs',
        icon: FileText,
        href: '/intake-programs',
        roles: ['admin'],
      },
      {
        label: 'Product Requests',
        icon: FileText,
        href: '/requests',
        roles: ['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly'],
      },
      {
        label: 'Catalogs',
        icon: BookOpen,
        href: '/catalogs',
        roles: ['admin', 'supervisor', 'distributor'],
      },
      {
        label: 'Pharmacy Config',
        icon: Building2,
        href: '/admin/pharmacy-config',
        roles: ['admin'],
      },
      {
        label: 'Shipping Settings',
        icon: Truck,
        href: '/admin/shipping-settings',
        roles: ['admin'],
      },
      {
        label: 'Supply Rules',
        icon: Package,
        href: '/admin/supply-rules',
        roles: ['admin'],
      },
      {
        label: 'Telemed Networks',
        icon: Stethoscope,
        href: '/admin/telemed-networks',
        roles: ['admin'],
        // Gated by the per-admin canManageTelemedNetworks permission; superadmins
        // grant the bit via Admins → Permissions.
        visibleIf: (u) => u?.adminData?.canManageTelemedNetworks === true,
      },
    ],
  },
  {
    label: 'Users',
    icon: Users,
    defaultOpen: false,
    items: [
      // One directory across every role, with the derived account state and the
      // blockers behind it. First in the section: it is the entry point, and the
      // role-scoped pages below remain for the existing per-role workflows.
      {
        label: 'All Users',
        icon: Users2,
        href: '/users',
        roles: ['admin', 'supervisor'],
        // The role-scoped lists live under the directory rather than as
        // siblings of payouts and AR/AP.
        children: [
          {
            label: 'Supervisors',
            icon: Shield,
            href: '/supervisors',
            roles: ['admin'],
          },
          {
            label: 'Admins',
            icon: Shield,
            href: '/admins',
            roles: ['admin'],
            visibleIf: (u) => u?.isSuperAdmin === true,
          },
          {
            label: 'Account Managers',
            icon: Building2,
            href: '/distributors',
            roles: ['admin', 'supervisor'],
          },
          {
            label: 'Clinics | Physicians',
            icon: UserCheck,
            href: '/physicians',
            roles: ['admin', 'supervisor', 'distributor'],
          },
          {
            // "Approval" undersold it: the queue now covers ID reviews, network
            // assignments and missing credentials as well.
            label: 'Pending review',
            icon: Clock,
            href: '/pending',
            roles: ['admin'],
          },
        ],
      },
      {
        label: 'Payout Management',
        icon: CreditCard,
        href: '/admin/payouts',
        roles: ['admin'],
      },
      {
        label: 'Commission Overrides',
        icon: Percent,
        href: '/admin/commission-overrides',
        roles: ['admin'],
      },
      {
        label: 'Negotiated Deals',
        icon: Tag,
        href: '/admin/clinic-deals',
        roles: ['admin'],
      },
      { label: 'Clinic Domains', icon: Store, href: '/admin/clinic-sites', roles: ['admin'] },
      {
        label: 'Facility Shipments',
        icon: Package,
        href: '/admin/shipments',
        roles: ['admin'],
      },
      {
        label: 'My Payouts',
        icon: CreditCard,
        href: '/payouts',
        // A payout-only 'partner' (lead source / departed rep receiving a
        // commission override) has no other surface in the app — this is their
        // whole frontend, so it must be reachable from the nav.
        roles: ['supervisor', 'distributor', 'partner'],
      },
      {
        label: 'Accounts Receivable',
        icon: Receipt,
        href: '/admin/ar',
        roles: ['admin'],
      },
      {
        label: 'Accounts Payable',
        icon: Receipt,
        href: '/admin/ap',
        roles: ['admin'],
      },
    ],
  },
  {
    label: 'Reporting',
    icon: BarChart3,
    defaultOpen: false,
    items: [
      {
        label: 'Analytics',
        icon: BarChart3,
        href: '/analytics',
        roles: ['admin', 'supervisor', 'distributor', 'physician'],
      },
      {
        label: 'Impersonations',
        icon: Shield,
        href: '/admin/impersonation',
        roles: ['admin'],
      },
      {
        label: 'Blasts',
        icon: Megaphone,
        href: '/admin/blasts',
        roles: ['admin'],
      },
      {
        label: 'Banners',
        icon: Flag,
        href: '/admin/banners',
        roles: ['admin'],
      },
      {
        label: 'User Activity',
        icon: Monitor,
        href: '/admin/user-activity',
        roles: ['admin'],
      },
      {
        label: 'All Patients',
        icon: HeartPulse,
        href: '/admin/patients',
        roles: ['admin'],
      },
      {
        label: 'Patient Analytics',
        icon: BarChart3,
        href: '/admin/patient-analytics',
        roles: ['admin'],
      },
      {
        label: 'Catalog Hierarchy',
        icon: Folder,
        href: '/admin/catalog-hierarchy',
        roles: ['admin'],
      },
      {
        label: 'Support',
        icon: MessageSquare,
        href: '/admin/support',
        roles: ['admin'],
      },
    ],
  },
  {
    label: 'Learn',
    icon: GraduationCap,
    defaultOpen: false,
    items: [
      {
        label: 'Video Tutorials',
        icon: GraduationCap,
        href: '/learn/videos',
        roles: ['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly'],
      },
      {
        label: 'Integration API',
        icon: Code,
        href: `${import.meta.env.VITE_API_URL || '/api'}/integration/docs`,
        roles: ['supervisor'],
        // Only partners have anything to call. The flag is what makes a
        // supervisor a partner, so it is what gates the reference.
        visibleIf: (user: any) => user?.supervisorData?.apiEnabled === true,
        external: true,
      },
      {
        label: 'API Tokens',
        icon: KeyRound,
        href: '/api-tokens',
        roles: ['supervisor'],
        // Same gate as the reference above: one flag, so enabling API access
        // gives a partner both the docs and the credentials to use them.
        visibleIf: (user: any) => user?.supervisorData?.apiEnabled === true,
      },
      {
        label: 'Peptide Calculator',
        icon: FlaskConical,
        href: '/admin/peptide-calculator',
        roles: ['admin'],
      },
    ],
  },
];

// Bottom items (Settings)
const bottomItems: NavItem[] = [
  {
    label: 'Settings',
    icon: Settings,
    href: '/settings',
    roles: ['admin', 'supervisor', 'distributor', 'physician'],
  },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const teamMember = useTeamMember();
  const { logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const { theme, toggleTheme, isDark } = useTheme();
  const [pendingCount, setPendingCount] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  // Nav items with children (All Users) whose sub-list is open. Collapsed by default.
  const [expandedNavItems, setExpandedNavItems] = useState<Set<string>>(new Set());

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  // Track user activity (sessions, page views, heartbeats)
  useActivityTracker();

  const careInboxUnread = useCareInboxUnreadCount(user?.role === 'physician' || user?.role === 'physician_readonly');

  // Get tenant branding
  const { branding, colors } = useTenantBranding();

  // Initialize expanded categories based on defaultOpen and current route
  useEffect(() => {
    const initialExpanded = new Set<string>();
    navigationCategories.forEach(category => {
      // Check if any item in this category matches current route
      const hasActiveItem = flattenNav(category.items).some(item =>
        item.roles.includes(user?.role || 'physician') && location.pathname === item.href
      );
      if (category.defaultOpen || hasActiveItem) {
        initialExpanded.add(category.label);
      }
    });
    setExpandedCategories(initialExpanded);
  }, [user?.role]);

  // Fetch pending users count for admins
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'supervisor') {
      const fetchPendingCount = async () => {
        try {
          // Count-only endpoint: avoids pulling the full pending list every 60s.
          const response = await apiClient.get<{ success: boolean; data: { count: number } }>('/users/pending/count');
          if (response.success && response.data) {
            setPendingCount(response.data.count);
          }
        } catch {
          // Non-critical nav badge; failures (403 from role drift, network, etc.) are not actionable
        }
      };
      fetchPendingCount();
      // Refresh every 60 seconds
      const interval = setInterval(fetchPendingCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleCategory = (categoryLabel: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryLabel)) {
        newSet.delete(categoryLabel);
      } else {
        newSet.add(categoryLabel);
      }
      return newSet;
    });
  };

  const getRoleDisplayName = (role: UserRole): string => {
    switch (role) {
      case 'admin':
        return 'System Administrator';
      case 'supervisor':
        return 'Regional Supervisor';
      case 'distributor':
        return 'Account Manager';
      case 'physician':
        return 'Clinic | Physician';
      case 'physician_readonly':
        return 'Clinic | Physician (Read-Only)';
      case 'partner':
        return 'Partner';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role: UserRole): string => {
    switch (role) {
      case 'admin':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'supervisor':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'distributor':
        return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'physician':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'physician_readonly':
        return 'bg-stone-100 text-stone-600 border-stone-200';
      case 'partner':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      default:
        return 'bg-stone-100 text-stone-600 border-stone-200';
    }
  };

  // Filter items based on user role + optional per-item predicate. Children
  // are filtered too; when the parent is hidden, its visible children stand
  // on their own (an AM sees Clinics without All Users).
  const canSee = (item: NavItem) =>
    item.roles.includes(user.role) && (!item.visibleIf || item.visibleIf(user));
  const filterItemsByRole = (items: NavItem[]): NavItem[] =>
    items.flatMap(item => {
      const children = item.children ? filterItemsByRole(item.children) : undefined;
      if (!canSee(item)) return children ?? [];
      return [{ ...item, children: children?.length ? children : undefined }];
    });

  // Filter categories that have at least one visible item
  const visibleCategories = navigationCategories
    .map(category => ({
      ...category,
      items: filterItemsByRole(category.items)
    }))
    .filter(category => category.items.length > 0);

  const allowedStandaloneItems = filterItemsByRole(standaloneItems);
  const allowedBottomItems = filterItemsByRole(bottomItems);

  // Find current page title
  const findCurrentPageTitle = (): string => {
    // Check standalone items
    const standaloneMatch = allowedStandaloneItems.find(item => item.href === location.pathname);
    if (standaloneMatch) return standaloneMatch.label;

    // Check category items
    for (const category of visibleCategories) {
      const itemMatch = flattenNav(category.items).find(item => item.href === location.pathname);
      if (itemMatch) return itemMatch.label;
    }

    // Check bottom items
    const bottomMatch = allowedBottomItems.find(item => item.href === location.pathname);
    if (bottomMatch) return bottomMatch.label;

    if (location.pathname === '/ava') return 'Ava';
    return 'Dashboard';
  };

  const currentPageTitle = findCurrentPageTitle();

  // Render a single nav item
  const renderNavItem = (item: NavItem, onClick?: () => void, indent = false) => {
    const isActive = !item.external && location.pathname === item.href;
    const Icon = item.icon;
    const badgeCount = item.href === '/pending' ? pendingCount : item.href === '/care-inbox' ? careInboxUnread : 0;
    const showPendingBadge = badgeCount > 0;
    // `Link` would hand an off-SPA path to the router and render nothing.
    const Wrapper: any = item.external ? 'a' : Link;
    const target = item.external
      ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
      : { to: item.href };

    return (
      <Wrapper
        key={item.href}
        {...target}
        onClick={onClick}
        title={sidebarCollapsed ? item.label : undefined}
        className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group ${
          isActive ? 'shadow-sm border' : 'hover:bg-stone-100'
        } ${indent && !sidebarCollapsed ? 'ml-4' : ''}`}
        style={isActive ? {
          backgroundColor: `${colors.primary}10`,
          color: colors.primary,
          borderColor: `${colors.primary}30`
        } : { color: 'var(--text-secondary)' }}
      >
        <span
          className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors ${
            isActive ? '' : 'text-stone-400 group-hover:text-stone-600'
          }`}
          style={isActive ? { color: colors.primary } : undefined}
        >
          <Icon className="h-4 w-4" />
        </span>
        {!sidebarCollapsed && <span className="flex-1">{item.label}</span>}
        {!sidebarCollapsed && showPendingBadge && (
          <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
            {badgeCount}
          </span>
        )}
        {!sidebarCollapsed && item.external && (
          <ExternalLink className="h-3 w-3 text-stone-400" />
        )}
      </Wrapper>
    );
  };

  // An item with children: its own link, a chevron for the sub-list, and the
  // sub-list itself. Open while the viewer toggled it or is on one of its pages.
  const renderNavGroup = (item: NavItem, onClick?: () => void, indent = false) => {
    const children = item.children ?? [];
    if (!children.length) return renderNavItem(item, onClick, indent);
    if (sidebarCollapsed) {
      return (
        <div key={item.href} className="space-y-0.5">
          {renderNavItem(item, onClick, indent)}
          {children.map(child => renderNavItem(child, onClick))}
        </div>
      );
    }
    const childActive = children.some(child => location.pathname === child.href);
    const isOpen = expandedNavItems.has(item.href) || childActive;
    const toggle = () => setExpandedNavItems(prev => {
      const next = new Set(prev);
      if (next.has(item.href)) next.delete(item.href);
      else next.add(item.href);
      return next;
    });
    return (
      <div key={item.href}>
        <div className="relative">
          {renderNavItem(item, onClick, indent)}
          {/* Pending review is nested now; keep its count visible while closed. */}
          {!isOpen && pendingCount > 0 && children.some(child => child.href === '/pending') && (
            <span className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
              {pendingCount}
            </span>
          )}
          <button
            type="button"
            onClick={toggle}
            disabled={childActive}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} ${item.label} sub-pages`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-stone-200 disabled:hover:bg-transparent disabled:cursor-default"
            style={{ color: 'var(--text-tertiary)' }}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5 ml-4 pl-2 border-l border-stone-200">
            {children.map(child => renderNavItem(child, onClick, indent))}
          </div>
        )}
      </div>
    );
  };

  // Render a category with its items
  const renderCategory = (category: { label: string; icon: React.ComponentType<{ className?: string }>; items: NavItem[] }, onClick?: () => void) => {
    const isExpanded = expandedCategories.has(category.label);
    const CategoryIcon = category.icon;
    const hasActiveItem = flattenNav(category.items).some(item => location.pathname === item.href);

    if (sidebarCollapsed) {
      // Collapsed: show each item as an icon
      return (
        <div key={category.label} className="mb-1 space-y-0.5">
          {category.items.map(item => renderNavGroup(item, onClick))}
        </div>
      );
    }

    return (
      <div key={category.label} className="mb-1">
        {/* Category header */}
        <button
          onClick={() => toggleCategory(category.label)}
          className={`flex items-center w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group hover:bg-stone-100 ${
            hasActiveItem && !isExpanded ? 'bg-stone-50' : ''
          }`}
          style={{ color: hasActiveItem ? colors.primary : 'var(--text-secondary)' }}
        >
          <span
            className="mr-3 transition-colors"
            style={{ color: hasActiveItem ? colors.primary : 'var(--text-tertiary)' }}
          >
            <CategoryIcon className="h-4 w-4" />
          </span>
          <span className="flex-1 text-left font-semibold">{category.label}</span>
          <span className="transition-transform duration-200" style={{ color: 'var(--text-tertiary)' }}>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </button>

        {/* Category items */}
        {isExpanded && (
          <div className="mt-1 space-y-0.5 pl-2">
            {category.items.map(item => renderNavGroup(item, onClick, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--neutral-50)' }}>
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Desktop Sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col transition-all duration-200 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-72'}`} style={{ background: 'var(--surface-primary)', borderRight: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-lg)' }}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center h-20 ${sidebarCollapsed ? 'justify-center px-2' : 'justify-center px-6'}`}
               style={{
                 background: isDark
                   ? 'var(--surface-tertiary)'
                   : `linear-gradient(135deg, ${colors.primary}, ${colors.lighter})`,
                 borderBottom: '1px solid var(--border-primary)'
               }}>
            <div className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg p-1 flex-shrink-0" style={{ background: 'var(--surface-primary)' }}>
                <img
                  src={branding.logo || "/5-axis-logo.svg"}
                  alt={`${branding.companyName} Logo`}
                  className="w-full h-full object-contain"
                />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <span className="text-2xl font-bold" style={{ color: isDark ? 'var(--text-primary)' : 'white' }}>{branding.title}</span>
                  <div className="text-xs font-medium" style={{ color: isDark ? 'var(--text-tertiary)' : 'rgba(255,255,255,0.8)' }}>{branding.companyName}</div>
                </div>
              )}
            </div>
          </div>

          {/* User Info */}
          {sidebarCollapsed ? (
            <div className="flex justify-center p-2" style={{ background: 'var(--neutral-100)', borderBottom: '1px solid var(--border-primary)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                   style={{
                     background: isDark
                       ? 'var(--neutral-300)'
                       : `linear-gradient(135deg, ${colors.primary}, ${colors.lighter})`
                   }}>
                <span className="font-semibold text-sm" style={{ color: isDark ? 'var(--text-primary)' : 'white' }}>
                  {teamMember ? `${teamMember.firstName[0]}${teamMember.lastName[0]}` : `${user.profile.firstName[0]}${user.profile.lastName[0]}`}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-6" style={{ background: 'var(--neutral-100)', borderBottom: '1px solid var(--border-primary)' }}>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                     style={{
                       background: isDark
                         ? 'var(--neutral-300)'
                         : `linear-gradient(135deg, ${colors.primary}, ${colors.lighter})`
                     }}>
                  <span className="font-semibold text-lg" style={{ color: isDark ? 'var(--text-primary)' : 'white' }}>
                    {teamMember ? `${teamMember.firstName[0]}${teamMember.lastName[0]}` : `${user.profile.firstName[0]}${user.profile.lastName[0]}`}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                    {teamMember ? `${teamMember.firstName} ${teamMember.lastName}` : `${user.profile.firstName} ${user.profile.lastName}`}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                    {teamMember ? teamMember.email : user.email}
                  </div>
                  {teamMember && (
                    <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-quaternary)' }}>
                      on behalf of {user.profile.firstName} {user.profile.lastName}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {/* Standalone items (Home) */}
            {allowedStandaloneItems.map(item => renderNavItem(item))}

            {/* Divider */}
            {allowedStandaloneItems.length > 0 && visibleCategories.length > 0 && (
              <div className="my-3 border-t" style={{ borderColor: 'var(--neutral-200)' }} />
            )}

            {/* Categorized items */}
            {visibleCategories.map(category => renderCategory(category))}
          </nav>

          {/* Bottom Section: Settings, Theme, Logout, Collapse */}
          <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} space-y-2`} style={{ borderTop: '1px solid var(--neutral-200)' }}>
            {/* Settings */}
            {allowedBottomItems.map(item => renderNavItem(item))}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              title={sidebarCollapsed ? `${theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'} Mode` : undefined}
              className={`flex items-center w-full ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group`}
              style={{
                background: 'var(--neutral-100)',
                color: 'var(--neutral-600)'
              }}
            >
              <span className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors`} style={{ color: 'var(--neutral-500)' }}>
                {theme === 'light' && <Sun className="h-4 w-4" />}
                {theme === 'dark' && <Moon className="h-4 w-4" />}
                {theme === 'system' && <Monitor className="h-4 w-4" />}
              </span>
              {!sidebarCollapsed && (
                <>
                  <span className="flex-1 text-left">
                    {theme === 'light' && 'Light Mode'}
                    {theme === 'dark' && 'Dark Mode'}
                    {theme === 'system' && 'System Theme'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--neutral-200)', color: 'var(--neutral-500)' }}>
                    {isDark ? 'Dark' : 'Light'}
                  </span>
                </>
              )}
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              title={sidebarCollapsed ? 'Sign Out' : undefined}
              className={`flex items-center w-full ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} py-2.5 text-sm font-medium rounded-lg hover:bg-rose-50 hover:text-rose-700 transition-all duration-200 group`}
              style={{ color: 'var(--neutral-600)' }}
            >
              <span className={`${sidebarCollapsed ? '' : 'mr-3'} text-stone-400 group-hover:text-rose-500 transition-colors`}>
                <LogOut className="h-4 w-4" />
              </span>
              {!sidebarCollapsed && 'Sign Out'}
            </button>

            {/* Collapse Toggle */}
            <button
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={`flex items-center w-full ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} py-2.5 text-sm font-medium rounded-lg hover:bg-stone-100 transition-all duration-200 group`}
              style={{ color: 'var(--neutral-500)' }}
            >
              <span className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors`}>
                {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </span>
              {!sidebarCollapsed && 'Collapse'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`} style={{ background: 'var(--surface-primary)', borderRight: '1px solid var(--neutral-200)' }}>
        <div className="flex flex-col h-full">
          {/* Mobile Header with Close Button */}
          <div className="flex items-center justify-between h-16 px-4"
               style={{
                 background: isDark
                   ? 'var(--surface-tertiary)'
                   : `linear-gradient(135deg, ${colors.primary}, ${colors.lighter})`,
                 borderBottom: '1px solid var(--border-primary)'
               }}>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-lg p-1" style={{ background: 'var(--surface-primary)' }}>
                <img
                  src={branding.logo || "/5-axis-logo.svg"}
                  alt={`${branding.companyName} Logo`}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <span className="text-lg font-bold" style={{ color: isDark ? 'var(--text-primary)' : 'white' }}>{branding.title}</span>
              </div>
            </div>
            <button
              onClick={closeMobileMenu}
              className="p-2 rounded-lg transition-colors"
              style={{ color: isDark ? 'var(--text-secondary)' : 'white' }}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile User Info */}
          <div className="p-4" style={{ background: 'var(--neutral-100)', borderBottom: '1px solid var(--border-primary)' }}>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                   style={{
                     background: isDark
                       ? 'var(--neutral-300)'
                       : `linear-gradient(135deg, ${colors.primary}, ${colors.lighter})`
                   }}>
                <span className="font-semibold text-sm" style={{ color: isDark ? 'var(--text-primary)' : 'white' }}>
                  {teamMember ? `${teamMember.firstName[0]}${teamMember.lastName[0]}` : `${user.profile.firstName[0]}${user.profile.lastName[0]}`}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                  {teamMember ? `${teamMember.firstName} ${teamMember.lastName}` : `${user.profile.firstName} ${user.profile.lastName}`}
                </div>
                <div className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                  {teamMember ? teamMember.email : user.email}
                </div>
                {teamMember && (
                  <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-quaternary)' }}>
                    on behalf of {user.profile.firstName} {user.profile.lastName}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                {getRoleDisplayName(user.role)}
              </span>
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {/* Standalone items (Home) */}
            {allowedStandaloneItems.map(item => renderNavItem(item, closeMobileMenu))}

            {/* Divider */}
            {allowedStandaloneItems.length > 0 && visibleCategories.length > 0 && (
              <div className="my-3 border-t" style={{ borderColor: 'var(--neutral-200)' }} />
            )}

            {/* Categorized items */}
            {visibleCategories.map(category => renderCategory(category, closeMobileMenu))}
          </nav>

          {/* Mobile Bottom Section */}
          <div className="p-3 border-t space-y-1" style={{ borderColor: 'var(--neutral-200)' }}>
            {/* Settings */}
            {allowedBottomItems.map(item => renderNavItem(item, closeMobileMenu))}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center w-full px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 group"
              style={{ background: 'var(--neutral-100)', color: 'var(--neutral-600)' }}
            >
              <span className="mr-3" style={{ color: 'var(--neutral-500)' }}>
                {theme === 'light' && <Sun className="h-5 w-5" />}
                {theme === 'dark' && <Moon className="h-5 w-5" />}
                {theme === 'system' && <Monitor className="h-5 w-5" />}
              </span>
              <span className="flex-1 text-left">
                {theme === 'light' && 'Light Mode'}
                {theme === 'dark' && 'Dark Mode'}
                {theme === 'system' && 'System Theme'}
              </span>
            </button>

            {/* Logout */}
            <button
              onClick={() => {
                handleLogout();
                closeMobileMenu();
              }}
              className="flex items-center w-full px-4 py-3 text-sm font-medium rounded-lg hover:bg-red-50 hover:text-red-700 transition-all duration-200 group"
              style={{ color: 'var(--neutral-600)' }}
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400 group-hover:text-red-500 transition-colors" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72'}`}>
        {/* Impersonation Banner - Shows when admin is impersonating */}
        <ImpersonationBanner />

        {/* Mobile Top Bar */}
        <header className="lg:hidden shadow-sm sticky top-0 z-50" style={{ background: 'var(--surface-primary)', borderBottom: '1px solid var(--neutral-200)' }}>
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shadow-sm p-1" style={{ background: 'var(--surface-primary)' }}>
                <img
                  src={branding.logo || "/5-axis-logo.svg"}
                  alt={`${branding.companyName} Logo`}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{branding.title}</span>
            </div>

            <div className="w-10 h-10 rounded-full flex items-center justify-center"
                 style={{
                   background: isDark
                     ? 'var(--neutral-300)'
                     : `linear-gradient(135deg, ${colors.primary}, ${colors.lighter})`
                 }}>
              <span className="font-semibold text-sm" style={{ color: isDark ? 'var(--text-primary)' : 'white' }}>
                {user.profile.firstName[0]}{user.profile.lastName[0]}
              </span>
            </div>
          </div>
        </header>

        {/* Desktop Top Bar */}
        <header className="hidden lg:block shadow-sm sticky top-0 z-50" style={{ background: 'var(--surface-primary)', borderBottom: '1px solid var(--neutral-200)' }}>
          <div className="px-6 lg:px-8 py-4 lg:py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {currentPageTitle}
                </h1>
                <p className="mt-1 text-sm lg:text-base" style={{ color: 'var(--text-tertiary)' }}>
                  {currentPageTitle === 'Home'
                    ? `Welcome back, ${teamMember ? teamMember.firstName : user.profile.firstName}! Here's your ${user.role.replace('_', ' ')} overview.`
                    : currentPageTitle === 'Ava'
                      ? 'Your Bloom assistant'
                      : `Manage your ${currentPageTitle.toLowerCase()} here.`
                  }
                </p>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center space-x-3">
                {user?.role === 'admin' && <NotificationBell />}
                {/* Quick actions will be handled by individual pages */}
              </div>
            </div>
          </div>
        </header>

        {/* Admin-posted announcements (Admin → Banners) */}
        <AppBanners />

        {/* Page Content */}
        {/* Extra bottom padding clears the fixed Zendesk support launcher
            (~64px + offset, bottom-right) so it never covers page content
            such as the last table row's rightmost column (MD-2071). */}
        <main className="px-4 pt-4 pb-24 lg:px-8 lg:pt-8 lg:pb-28">
          <div className="max-w-7xl xl:max-w-[1600px] 2xl:max-w-[2000px] mx-auto">
            {children}
          </div>
        </main>
      </div>
      <ZendeskWidget />
    </div>
  );
};
